import { useState } from 'react';
import { Plus, Check, X, Pencil, EyeOff, Eye } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { useCustomers } from '../../hooks/useCustomers';
import { mensajeErrorFinanzas } from '../../utils/finance';

/** FE-32 — los clientes del tenant: a quién se le cobra. Sin borrado: se desactivan. */
export function CustomersPanel({ search, soloLectura = false }: { search: string; soloLectura?: boolean }) {
  const { customers, isLoading, error, crear, actualizar } = useCustomers();
  const [creando, setCreando] = useState(false);
  const [nuevo, setNuevo] = useState('');
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [editNombre, setEditNombre] = useState('');
  const [enCurso, setEnCurso] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const [estado, setEstado] = useState<'activos' | 'inactivos' | 'todos'>('activos');

  const visibles = customers
    .filter(c => estado === 'todos' || c.active === (estado === 'activos'))
    .filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));

  const ejecutar = async (fn: () => Promise<unknown>) => {
    setEnCurso(true); setAviso(null);
    try { await fn(); return true; }
    catch (err) { setAviso(mensajeErrorFinanzas(err, 'cliente')); return false; }
    finally { setEnCurso(false); }
  };

  if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><Spinner /></div>;

  return (
    <div className="expenses-touch" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.6 }}>
        A quién se le cobra. Un cliente con ingresos es historia contable: se <strong>desactiva</strong>, no se borra.
      </p>
      {error && <p style={{ margin: 0, fontSize: '12.5px', color: '#EF4444' }}>No se pudieron cargar los clientes: {error.message}</p>}
      {aviso && <p style={{ margin: 0, fontSize: '12.5px', color: '#EF4444', fontWeight: 600 }}>{aviso}</p>}

      <div style={{ maxWidth: 220 }}>
        <Select label="Estado" value={estado} onChange={e => setEstado(e.target.value as typeof estado)}
          options={[{ value: 'activos', label: 'Activos' }, { value: 'inactivos', label: 'Desactivados' }, { value: 'todos', label: 'Todos' }]} />
      </div>

      {!soloLectura && (creando ? (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end', background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '0.75rem' }}>
          <div style={{ flex: '1 1 220px' }}><Input label="Nombre" value={nuevo} onChange={e => setNuevo(e.target.value)} placeholder="Camfri" /></div>
          <Button size="sm" isLoading={enCurso} onClick={async () => {
            if (!nuevo.trim()) { setAviso('El nombre es obligatorio.'); return; }
            if (await ejecutar(() => crear({ name: nuevo.trim() }))) { setNuevo(''); setCreando(false); }
          }}>Crear</Button>
          <Button size="sm" variant="ghost" onClick={() => { setCreando(false); setAviso(null); }}>Cancelar</Button>
        </div>
      ) : (
        <Button size="sm" leftIcon={<Plus size={15} />} onClick={() => setCreando(true)} style={{ alignSelf: 'flex-start' }}>Nuevo cliente</Button>
      ))}

      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        {visibles.map(c => {
          const editando = editandoId === c.id;
          return (
            <li key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', background: 'var(--bg-raised)', border: '1px solid var(--border)', borderLeft: `3px solid ${c.active ? '#10B981' : 'var(--border-strong)'}`, borderRadius: 'var(--radius-md)', padding: '0.625rem 0.875rem', opacity: c.active ? 1 : 0.6 }}>
              {editando ? (
                <>
                  <input value={editNombre} onChange={e => setEditNombre(e.target.value)} style={{ flex: '1 1 160px', padding: '0.35rem 0.6rem', fontSize: '13px', background: 'var(--bg-surface)', color: 'var(--text-primary)', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', fontFamily: 'inherit' }} />
                  <Button size="sm" leftIcon={<Check size={13} />} isLoading={enCurso} onClick={async () => {
                    if (!editNombre.trim()) { setAviso('El nombre no puede quedar vacío.'); return; }
                    if (await ejecutar(() => actualizar(c.id, { name: editNombre.trim() }))) setEditandoId(null);
                  }}>Guardar</Button>
                  <Button size="sm" variant="ghost" leftIcon={<X size={13} />} onClick={() => setEditandoId(null)}>Cancelar</Button>
                </>
              ) : (
                <>
                  <span style={{ flex: 1, fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {c.name}{!c.active && <span style={{ marginLeft: '0.5rem', fontSize: '11px', fontWeight: 600, color: 'var(--text-muted)' }}>(inactivo)</span>}
                  </span>
                  {!soloLectura && (
                    <>
                      <Button size="sm" variant="ghost" leftIcon={<Pencil size={13} />} onClick={() => { setEditandoId(c.id); setEditNombre(c.name); setAviso(null); }}>Renombrar</Button>
                      <Button size="sm" variant="ghost" leftIcon={c.active ? <EyeOff size={13} /> : <Eye size={13} />} disabled={enCurso} onClick={() => ejecutar(() => actualizar(c.id, { active: !c.active }))}>
                        {c.active ? 'Desactivar' : 'Reactivar'}
                      </Button>
                    </>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>
      {visibles.length === 0 && (
        <p style={{ margin: 0, padding: '1rem', textAlign: 'center', fontSize: '13px', color: 'var(--text-muted)' }}>
          {search ? 'Ningún cliente coincide con la búsqueda.' : estado === 'inactivos' ? 'No hay clientes desactivados.' : estado === 'activos' && customers.length > 0 ? 'No hay clientes activos.' : 'Todavía no hay clientes.'}
        </p>
      )}
    </div>
  );
}
